import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabGroup, TabButton } from '../../ui/Tabs/index.js';
import { formatPeriodTick } from './chart-utils.js';
import type { BugBreakdownPoint, BugTrendUnit } from '../../../types/api.js';

/**
 * バグ内訳カテゴリの表示順（重要度順）。凡例・ツールチップ・積み上げ（下→上）で共通。
 * preRelease / notBug / duplicate はチャート上は「その他」(other) に合算して表示する
 * （内訳は月クリックのドリルダウンモーダルで確認できる）。
 * @graph-connects none
 */
const CATEGORY_ORDER = [
  {
    key: 'devQuality',
    labelKey: 'reliability.category.devQuality',
    color: 'var(--color-accent-primary)',
  },
  // KPIの90日窓をすり抜けた開発品質バグ（KPI対象と同系統の薄い色で隣接表示）
  { key: 'kpiEscaped', labelKey: 'reliability.category.kpiEscaped', color: '#818cf8' },
  { key: 'latent', labelKey: 'reliability.category.latent', color: 'var(--color-error)' },
  { key: 'specGap', labelKey: 'reliability.category.specGap', color: 'var(--color-warning)' },
  { key: 'business', labelKey: 'reliability.category.business', color: 'var(--color-text-muted)' },
  // 潜在バグ（自動検知）= NR Analyzer 等の bot 起票。人起票の潜在バグと分離して表示
  { key: 'autoDetected', labelKey: 'reliability.category.autoDetected', color: '#14b8a6' },
  // チャート上の「その他」は preRelease / notBug / duplicate / other の合算なので専用ラベル
  { key: 'other', labelKey: 'reliability.category.otherMerged', color: '#64748b' },
] as const;

/**
 * 凡例・ツールチップの表示順（重要度順、捕捉率は最後）。
 * recharts v3 は既定で value/name ソートするため、明示的な itemSorter で上書きする。
 * @graph-connects none
 */
const ITEM_RANK: Record<string, number> = {
  devQuality: 0,
  kpiEscaped: 1,
  latent: 2,
  specGap: 3,
  business: 4,
  autoDetected: 5,
  other: 6,
  captureRate: 7,
};

/**
 * dataKey → 重要度順 rank（未知キーは末尾）
 * @graph-connects none
 */
function rankOf(dataKey: unknown): number {
  return ITEM_RANK[String(dataKey)] ?? 99;
}

export interface BugBreakdownChartProps {
  /** バグ内訳データ（unit に応じて月次または週次） */
  data: BugBreakdownPoint[];
  /** 取得月数（説明文用） */
  months: number;
  /** 読み込み状態 */
  isLoading: boolean;
  /** 集計単位（月次/週次・月曜始まり） */
  unit: BugTrendUnit;
  /** 集計単位切り替え時のコールバック */
  onUnitChange: (unit: BugTrendUnit) => void;
  /** 期間クリック時のコールバック（月次: YYYY-MM / 週次: 週開始日 YYYY-MM-DD）。ドリルダウンモーダル表示用 */
  onPeriodClick?: (period: string) => void;
}

interface ChartRow {
  month: string;
  captureRate: number | null;
  devQuality: number;
  kpiEscaped: number;
  latent: number;
  specGap: number;
  business: number;
  autoDetected: number;
  other: number;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 信頼性ページの「全バグ内訳＋捕捉率」チャート。案件起因＋潜在バグを月次/週次（月曜始まり）切り替え可能なカテゴリ別（重要度順）に積み上げ、捕捉率を折れ線で重ねる。実数/構成比も切り替え可能。リリース前/バグではない/重複は「その他」に合算し、期間クリックでドリルダウンモーダルを開く。90日窓の判定基準（KPI と同じ起票日−起因案件リリース日）を注記する
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects TabGroup [calls] 月次/週次・実数/構成比トグル描画
 * @graph-connects ComposedChart [calls] バグ内訳複合チャート描画
 * @graph-connects formatPeriodTick [calls] 週次X軸目盛りの短縮フォーマット
 */
export function BugBreakdownChart({
  data,
  months,
  isLoading,
  unit,
  onUnitChange,
  onPeriodClick,
}: BugBreakdownChartProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'absolute' | 'percentage'>('absolute');

  const chartData = useMemo<ChartRow[]>(() => {
    return data.map((point) => {
      const denom = mode === 'percentage' ? point.total : 1;
      const scale = (value: number): number =>
        mode === 'percentage' ? (denom > 0 ? Math.round((value / denom) * 1000) / 10 : 0) : value;
      return {
        month: point.month,
        captureRate: point.captureRate !== null ? Math.round(point.captureRate * 1000) / 10 : null,
        devQuality: scale(point.devQuality),
        kpiEscaped: scale(point.kpiEscaped),
        latent: scale(point.latent),
        specGap: scale(point.specGap),
        business: scale(point.business),
        autoDetected: scale(point.autoDetected),
        // リリース前・バグではない・重複・未分類はチャート上は「その他」に合算する
        other: scale(point.preRelease + point.notBug + point.duplicate + point.other),
      };
    });
  }, [data, mode]);

  const hasData = data.length > 0;

  const tooltipStyle = {
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
  };

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t(
          unit === 'week' ? 'reliability.breakdown.titleWeekly' : 'reliability.breakdown.title',
        )}
        description={t(
          unit === 'week'
            ? 'reliability.breakdown.descriptionWeekly'
            : 'reliability.breakdown.description',
          { months },
        )}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <TabGroup ariaLabel={t('reliability.unitSwitch')}>
              <TabButton
                active={unit === 'month'}
                label={t('reliability.unit.month')}
                onClick={() => {
                  onUnitChange('month');
                }}
              />
              <TabButton
                active={unit === 'week'}
                label={t('reliability.unit.week')}
                onClick={() => {
                  onUnitChange('week');
                }}
              />
            </TabGroup>
            <TabGroup ariaLabel={t('reliability.breakdown.viewSwitch')}>
              <TabButton
                active={mode === 'absolute'}
                label={t('reliability.breakdown.absolute')}
                onClick={() => {
                  setMode('absolute');
                }}
              />
              <TabButton
                active={mode === 'percentage'}
                label={t('reliability.breakdown.percentage')}
                onClick={() => {
                  setMode('percentage');
                }}
              />
            </TabGroup>
          </div>
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <div className="h-80" style={onPeriodClick ? { cursor: 'pointer' } : undefined}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 8, bottom: 5 }}
                onClick={(state) => {
                  const period = state?.activeLabel;
                  if (onPeriodClick && typeof period === 'string' && period.length > 0) {
                    onPeriodClick(period);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  // 週次は目盛りを MM-DD に短縮する（年跨ぎ考慮は formatPeriodTick 側。ツールチップは全体表示）
                  tickFormatter={(v: string) => formatPeriodTick(v, unit)}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(v: number) =>
                    mode === 'percentage' ? `${String(v)}%` : String(v)
                  }
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, 100]}
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(v: number) => `${String(v)}%`}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  itemSorter={(item) => rankOf(item.dataKey)}
                  formatter={(value: unknown, name: unknown) => {
                    const label = String(name);
                    const num = String(value);
                    if (label === t('reliability.breakdown.captureRate')) {
                      return [`${num}%`, label];
                    }
                    return [mode === 'percentage' ? `${num}%` : num, label];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  itemSorter={(item) => rankOf(item.dataKey)}
                />
                {CATEGORY_ORDER.map((cat) => (
                  <Bar
                    key={cat.key}
                    yAxisId="left"
                    dataKey={cat.key}
                    stackId="bugs"
                    name={t(cat.labelKey)}
                    fill={cat.color}
                  />
                ))}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="captureRate"
                  name={t('reliability.breakdown.captureRate')}
                  stroke="var(--color-text-primary)"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('reliability.breakdown.empty')}</p>
          </div>
        )}
        {/* 90日窓の判定基準（KPI と同じ起票日ベース）をページ上に明記する */}
        <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t('reliability.windowNote')}</p>
      </CardContent>
    </Card>
  );
}
